import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./Legal.css";
import PrivacySettingsModal from "./PrivacySettingsModal";

function PrivacySettings() {
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);

  return (
    <div className="legal-page">
      <Link to="/" className="back-home">
        ← Back to Home
      </Link>
      <div className="legal-content">
        <h1 className="legal-title">Privacy Settings</h1>

        <p className="legal-subtitle">Manage Your Privacy Preferences</p>

        <p className="legal-intro">
          At M31-V1, we believe you should have control over your personal
          information and privacy preferences. Use this page to understand and
          manage how your data is collected and used.
        </p>

        <div className="privacy-quick-settings">
          <button
            onClick={() => setIsPrivacyModalOpen(true)}
            className="privacy-settings-button"
          >
            Manage Cookie Preferences
          </button>
        </div>

        <PrivacySettingsModal
          isOpen={isPrivacyModalOpen}
          onClose={() => setIsPrivacyModalOpen(false)}
        />

        <section className="legal-section">
          <h2>1. Account Information</h2>
          <p>
            You can review and update your account information at any time by
            logging into your account settings. This includes your name, email
            address, and other profile information.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Cookie Preferences</h2>
          <p>
            We use cookies to improve your experience on our Services. You can
            manage your cookie preferences through your browser settings:
          </p>
          <ul>
            <li>
              <strong>Essential Cookies:</strong> Required for the Services to
              function properly. These cannot be disabled.
            </li>
            <li>
              <strong>Analytics Cookies:</strong> Help us understand how you use
              the Services so we can improve them.
            </li>
            <li>
              <strong>Functional Cookies:</strong> Remember your preferences and
              personalize your experience.
            </li>
          </ul>
          <p>
            To disable non-essential cookies, adjust your browser settings. Note
            that this may affect the functionality of the Services.
          </p>
        </section>

        <section className="legal-section">
          <h2>3. Communication Preferences</h2>
          <p>
            You can control the types of communications you receive from us:
          </p>
          <ul>
            <li>
              <strong>Service Updates:</strong> Important information about your
              account and the Services (cannot be disabled)
            </li>
            <li>
              <strong>Product Updates:</strong> Information about new features
              and improvements
            </li>
            <li>
              <strong>Marketing Communications:</strong> Promotional offers and
              news
            </li>
          </ul>
          <p>
            To update your communication preferences, visit your account
            settings or click the "unsubscribe" link in any email we send.
          </p>
        </section>

        <section className="legal-section">
          <h2>4. Data Access and Portability</h2>
          <p>
            You have the right to request a copy of your personal information.
            To request your data:
          </p>
          <ol>
            <li>
              Send an email to{" "}
              <a href="mailto:privacy@m31v1.com">privacy@m31v1.com</a> with the
              subject "Data Access Request"
            </li>
            <li>Include your account email address and any relevant details</li>
            <li>
              We will respond within 30 days with your data in a portable format
            </li>
          </ol>
        </section>

        <section className="legal-section">
          <h2>5. Data Deletion</h2>
          <p>
            You have the right to request deletion of your personal information.
            To delete your data:
          </p>
          <ol>
            <li>
              Send an email to{" "}
              <a href="mailto:privacy@m31v1.com">privacy@m31v1.com</a> with the
              subject "Data Deletion Request"
            </li>
            <li>
              Include your account email address and confirmation of your
              request
            </li>
            <li>We will process your request within 30 days</li>
          </ol>
          <p>
            Please note that some information may be retained as required by law
            or for legitimate business purposes. Deletion of your data will
            result in termination of your account and loss of access to the
            Services.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. Third-Party Services</h2>
          <p>
            If you have connected third-party services to your M31-V1 account,
            you can manage these connections in your account settings.
            Disconnecting a third-party service will prevent it from accessing
            your M31-V1 data.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Location-Based Services</h2>
          <p>
            Some features of our Services may use location data. You can control
            location permissions through your device settings or browser
            settings.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Do Not Track</h2>
          <p>
            Some browsers include a "Do Not Track" feature. Our Services do not
            currently respond to "Do Not Track" signals. You can manage tracking
            preferences through your browser settings and our cookie
            preferences.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. California Privacy Rights</h2>
          <p>
            If you are a California resident, you have additional rights under
            the California Consumer Privacy Act (CCPA), including:
          </p>
          <ul>
            <li>Right to know what personal information is collected</li>
            <li>Right to know if personal information is sold or disclosed</li>
            <li>Right to say no to the sale of personal information</li>
            <li>Right to access your personal information</li>
            <li>Right to equal service and price</li>
          </ul>
          <p>
            To exercise these rights, contact us at{" "}
            <a href="mailto:privacy@m31v1.com">privacy@m31v1.com</a>.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. European Privacy Rights</h2>
          <p>
            If you are in the European Economic Area (EEA), you have rights
            under the General Data Protection Regulation (GDPR), including:
          </p>
          <ul>
            <li>Right of access to your personal data</li>
            <li>Right to rectification of inaccurate data</li>
            <li>Right to erasure ("right to be forgotten")</li>
            <li>Right to restriction of processing</li>
            <li>Right to data portability</li>
            <li>Right to object to processing</li>
            <li>Rights related to automated decision-making</li>
          </ul>
          <p>
            To exercise these rights or if you have concerns about how we handle
            your data, contact us at{" "}
            <a href="mailto:privacy@m31v1.com">privacy@m31v1.com</a> or contact
            your local data protection authority.
          </p>
        </section>

        <div className="legal-footer">
          <p>
            For questions about your privacy settings or to exercise your
            privacy rights, please contact us at{" "}
            <a href="mailto:privacy@m31v1.com">privacy@m31v1.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default PrivacySettings;
